import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw, Search } from 'lucide-react';
import { useAuth } from '@edusense/shared-auth-core';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import type { SubscriptionCancelChoice } from '../components/SubscriptionCancelPanel';
import { getAdminAuthServerUrl } from '../auth/adminOAuth';
import { findAdminQLanguageUserByEmail } from '../qLanguage/adminQLanguageApi';
import { normalizeAdminAccountUsers, type AdminAccountUser } from '../types/account';
import {
  approveUgotProRefund,
  cancelUgotProSubscription,
  describeUgotProError,
  getUgotProBilling,
  grantUgotPro,
  listUgotProGrants,
  listUgotProRefundRequests,
  refundUgotProOrder,
  rejectUgotProRefund,
  revokeUgotPro,
} from '../ugotPro/ugotProApi';
import { parseUgotProRefundAmount, ugotProDateInputToExpiresAt } from '../ugotPro/ugotProFormat';
import type {
  UgotProAdminOrder,
  UgotProAdminSubscription,
  UgotProBilling,
  UgotProGrantList as GrantList,
  UgotProRefundRequest,
} from '../ugotPro/ugotProTypes';
import { UgotProGrantSection, type UgotProGrantInput } from './ugotPro/UgotProGrantSection';
import { UgotProBillingSection } from './ugotPro/UgotProBillingSection';
import { UgotProGrantList } from './ugotPro/UgotProGrantList';
import { UgotProRefundQueue, type UgotProApproveInput } from './ugotPro/UgotProRefundQueue';
import { UgotQuotaOverridesSection } from './ugotPro/UgotQuotaOverridesSection';

type Selected = { userId: string; email: string; billing: UgotProBilling | null };
type Notice = { tone: 'ok' | 'error'; text: string } | null;
type Loadable<T> = { data: T; loading: boolean; error: string | null };

/**
 * UGOT Pro 관리: 이메일로 사람을 찾아 내부 권한을 주고·빼고, 결제 구독 해지·주문 환불·환불 검토를 한 화면에서.
 * 서버(pay)가 돌려준 message 를 그대로 보여 준다 — Paddle 승인 대기 같은 상태를 꾸미지 않는다.
 */
export default function UgotProAdminPage() {
  const { token: accessToken } = useAuth();
  const [email, setEmail] = useState('');
  const [selected, setSelected] = useState<Selected | null>(null);
  const [lookup, setLookup] = useState<{ loading: boolean; error: string | null }>({ loading: false, error: null });
  const [grants, setGrants] = useState<Loadable<GrantList | null>>({ data: null, loading: true, error: null });
  const [queue, setQueue] = useState<Loadable<UgotProRefundRequest[]>>({ data: [], loading: true, error: null });
  const [users, setUsers] = useState<AdminAccountUser[]>([]);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice>(null);

  const emailByUserId = useMemo(() => new Map(users.map((user) => [user.id, user.email])), [users]);

  const loadGrants = useCallback(async () => {
    setGrants((current) => ({ ...current, loading: true, error: null }));
    try {
      setGrants({ data: await listUgotProGrants(), loading: false, error: null });
    } catch (error) {
      setGrants({ data: null, loading: false, error: describeUgotProError(error) });
    }
  }, []);

  const loadQueue = useCallback(async () => {
    setQueue((current) => ({ ...current, loading: true, error: null }));
    try {
      setQueue({ data: await listUgotProRefundRequests(), loading: false, error: null });
    } catch (error) {
      setQueue({ data: [], loading: false, error: describeUgotProError(error) });
    }
  }, []);

  useEffect(() => {
    void loadGrants();
    void loadQueue();
  }, [loadGrants, loadQueue]);

  useEffect(() => {
    // 목록의 userId 를 이메일로 보여 주려고 계정 목록(auth)을 한 번 읽는다. 실패하면 ID 를 그대로 보여 준다.
    const controller = new AbortController();
    axios.get<unknown>('/api/admin/users', {
      baseURL: getAdminAuthServerUrl(),
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      withCredentials: true,
      signal: controller.signal,
    }).then((response) => setUsers(normalizeAdminAccountUsers(response.data))).catch(() => undefined);
    return () => controller.abort();
  }, [accessToken]);

  const loadBilling = useCallback(async (userId: string, userEmail: string) => {
    setLookup({ loading: true, error: null });
    try {
      const billing = await getUgotProBilling(userId);
      setSelected({ userId, email: userEmail, billing });
      setLookup({ loading: false, error: null });
    } catch (error) {
      setSelected({ userId, email: userEmail, billing: null });
      setLookup({ loading: false, error: describeUgotProError(error) });
    }
  }, []);

  const findByEmail = useCallback(async () => {
    setNotice(null);
    setLookup({ loading: true, error: null });
    try {
      const user = await findAdminQLanguageUserByEmail(email);
      await loadBilling(user.id, user.email);
    } catch (error) {
      setSelected(null);
      setLookup({ loading: false, error: describeUgotProError(error) });
    }
  }, [email, loadBilling]);

  const openUser = useCallback((userId: string) => {
    setNotice(null);
    const userEmail = emailByUserId.get(userId) || '';
    setEmail(userEmail);
    void loadBilling(userId, userEmail);
  }, [emailByUserId, loadBilling]);

  /** 동작 하나를 실행하고, 성공하면 서버 문구를 보이고 관련 목록을 다시 읽는다. */
  const run = useCallback(async (key: string, action: () => Promise<string>) => {
    setBusyKey(key);
    setNotice(null);
    try {
      const message = await action();
      setNotice({ tone: 'ok', text: message });
    } catch (error) {
      setNotice({ tone: 'error', text: describeUgotProError(error) });
    } finally {
      setBusyKey(null);
    }
    await Promise.all([
      loadGrants(),
      loadQueue(),
      selected ? loadBilling(selected.userId, selected.email) : Promise.resolve(),
    ]);
  }, [loadBilling, loadGrants, loadQueue, selected]);

  const who = selected?.email || selected?.userId || '';

  const handleGrant = async (input: UgotProGrantInput) => {
    if (!selected) return;
    await run('grant', async () => {
    const grant = await grantUgotPro(selected.userId, { note: input.note, expiresAt: ugotProDateInputToExpiresAt(input.date) });
    return `${who} 에게 UGOT Pro 를 줬습니다 (만료 ${input.date}).${grant.active ? '' : ' 다만 서버 상태가 아직 유효하지 않습니다 — 목록을 확인하세요.'}`;
    });
  };

  const handleRevoke = () => {
    if (!selected || !window.confirm(`${who} 의 UGOT Pro 내부 권한을 지금 회수합니다.\n결제 구독은 그대로 둡니다. 계속할까요?`)) return;
    void run('revoke', async () => {
      await revokeUgotPro(selected.userId);
      return `${who} 의 내부 권한을 회수했습니다.`;
    });
  };

  const handleCancel = async (subscription: UgotProAdminSubscription, choice: SubscriptionCancelChoice) => {
    if (!selected) return;
    await run(`cancel:${subscription.subscriptionId}`, async () => (
      await cancelUgotProSubscription(selected.userId, subscription.subscriptionId, choice)).message);
  };

  const handleRefund = async (order: UgotProAdminOrder, reason: string) => {
    if (!selected) return;
    await run(`refund:${order.orderId}`, async () => (await refundUgotProOrder(selected.userId, order.orderId, reason)).message);
  };

  const handleApprove = (refund: UgotProRefundRequest, input: UgotProApproveInput) => run(
    `approve:${refund.refundRequestId}`,
    async () => (await approveUgotProRefund(refund.refundRequestId, {
      amount: parseUgotProRefundAmount(input.amount, refund.currency),
      note: input.note,
    })).message,
  );

  const handleReject = (refund: UgotProRefundRequest, reason: string) => run(
    `reject:${refund.refundRequestId}`,
    async () => (await rejectUgotProRefund(refund.refundRequestId, reason)).message,
  );

  return (
    <div className="space-y-6" data-testid="ugot-pro-admin-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">UGOT Pro</h1>
          <p className="text-sm text-muted-foreground">가족·내부자에게 권한을 주고 빼고, 결제 구독 해지·환불·환불 검토를 한 곳에서 합니다.</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => { void loadGrants(); void loadQueue(); }}>
          <RefreshCw className="h-4 w-4" /> 새로 고침
        </Button>
      </div>

      {notice && (
        <div
          role={notice.tone === 'error' ? 'alert' : 'status'}
          className={notice.tone === 'error'
            ? 'rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800'
            : 'rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900'}
          data-testid="ugot-pro-notice"
        >
          {notice.text}
        </div>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">사용자 찾기</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <form className="flex flex-col gap-2 sm:flex-row" onSubmit={(event) => { event.preventDefault(); void findByEmail(); }}>
            <Input type="email" placeholder="가입 이메일" value={email} onChange={(event) => setEmail(event.target.value)} data-testid="ugot-pro-email" />
            <Button type="submit" disabled={lookup.loading || !email.trim()} data-testid="ugot-pro-search">
              {lookup.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} 찾기
            </Button>
          </form>
          {lookup.error && <p className="text-sm text-red-700" role="alert">{lookup.error}</p>}
          {selected && (
            <div className="space-y-4" data-testid="ugot-pro-selected">
              <div className="text-sm"><span className="font-semibold">{selected.email || '(이메일 모름)'}</span> <span className="break-all text-muted-foreground">{selected.userId}</span></div>
              {selected.billing && (
                <>
                  <UgotProGrantSection
                    key={`${selected.userId}:${selected.billing.grant?.updatedAt ?? 'none'}`}
                    grant={selected.billing.grant}
                    busyKey={busyKey}
                    onGrant={handleGrant}
                    onRevoke={handleRevoke}
                  />
                  <UgotProBillingSection billing={selected.billing} busyKey={busyKey} onCancel={handleCancel} onRefund={handleRefund} />
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <UgotProRefundQueue
        items={queue.data}
        loading={queue.loading}
        error={queue.error}
        emailByUserId={emailByUserId}
        busyKey={busyKey}
        onApprove={handleApprove}
        onReject={handleReject}
        onOpenUser={openUser}
      />
      <UgotProGrantList grants={grants.data} loading={grants.loading} error={grants.error} emailByUserId={emailByUserId} onOpenUser={openUser} />
      <UgotQuotaOverridesSection />
    </div>
  );
}
